import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import { addResizedColumns } from "./useTableConfig";
import { META_LEADS_EVENTS_TABLE_CONSTANTS } from "routes/meta_leads_events/MetaLeadsEventsTableV3";
import {
  getMetaLeadsEventsTableColumns,
  getMetaLeadsEventsTableFilters,
} from "routes/meta_leads_events/MetaLeadsEventsTableColumnsAndFilters";
import MetaLeadsEventService, {
  META_LEADS_EVENTS_QUERY_KEY,
} from "services/MetaLeadsEvent";
import UserService from "services/User";

export const invalidateMetaLeadsEventsTableQuery = async () => {
  // The admin table and the entity-leads status dropdown read the same events,
  // so both caches must refresh when an event changes.
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [META_LEADS_EVENTS_TABLE_CONSTANTS.queryKey],
    }),
    queryClient.invalidateQueries({ queryKey: [META_LEADS_EVENTS_QUERY_KEY] }),
  ]);
};

export const useMetaLeadsEventMutations = () => {
  const create = useMutation({
    mutationFn: data => MetaLeadsEventService.create(data),
    onSuccess: () => toast.success("Meta Leads Event saved"),
    onError: err => toast.error(formatError(err, "Failed to save event")),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => MetaLeadsEventService.update(id, data),
    onSuccess: () => toast.success("Meta Leads Event saved"),
    onError: err => toast.error(formatError(err, "Failed to save event")),
  });

  const remove = useMutation({
    mutationFn: id => MetaLeadsEventService.remove(id),
    onSuccess: () => toast.success("Meta Leads Event deleted"),
    onError: err => toast.error(formatError(err, "Failed to delete event")),
  });

  return { create, update, remove };
};

export const useMetaLeadsEventsTable = ({ tableName }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => getMetaLeadsEventsTableFilters(), []);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getMetaLeadsEventsTableColumns();

    const eventsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    return addResizedColumns({
      columns: initialColumns,
      resizedColumns: eventsTableSettings?.resized_columns || {},
    });
  }, [tableName, tableSettings, isTableSettingsFetched]);

  return { filters, columns };
};
